import React, { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Company } from '../models/Company'
import {
    TenantBill,
    DelinquentAccount,
    PaymentMethod,
    StripeConnectAccount,
} from '../models/Rent/types'
import { axiosInstance } from '../helpers/axios'

// Type for valid sort fields
export type ValidTenantBillSortField =
    | 'created_date'
    | 'due_date'
    | 'subtotal'
    | 'balance_due'
    | 'status'
    | 'tenant_name'
    | 'unit_name'
    | 'billing_month'
    | '-created_date'
    | '-due_date'
    | '-subtotal'
    | '-balance_due'
    | '-status'
    | '-tenant_name'
    | '-unit_name'
    | '-billing_month'

export type StripeAccountStatus =
    | 'restricted_soon'
    | 'restricted'
    | 'in_review'
    | 'enabled'
    | 'rejected'

interface TenantBillsResponse {
    count: number
    next: string | null
    previous: string | null
    results: TenantBill[]
}

interface DelinquentAccountsResponse {
    count: number
    next: string | null
    previous: string | null
    results: DelinquentAccount[]
}

interface RentAnalytics {
    monthly_collections: number
    total_overdue: number
    tenants_overdue: number
    processing_fees: number
}

interface TenantBillsParams {
    page?: number
    page_size?: number
    sort?: string
    apartment_id?: number
    tenant_id?: number
    status?: string
    search?: string
    billing_month?: string
    payment_status?: string
    is_overdue?: string
    due_date_start?: string
    due_date_end?: string
    created_date_start?: string
    created_date_end?: string
    min_amount?: string
    max_amount?: string
    min_amount_paid?: string
    max_amount_paid?: string
    min_balance_due?: string
    max_balance_due?: string
}

interface CreatePaymentParams {
    bill_id: number
    amount: number
    payment_method: PaymentMethod
    notes?: string
}

interface VoidBillParams {
    bill_id: number
    void_reason?: string
}

interface VoidBillItemParams {
    bill_id: number
    bill_item_id: number
    void_reason?: string
}

interface VoidPaymentParams {
    bill_id: number
    payment_id: string
    void_reason?: string
}

interface SendOverdueNoticeParams {
    tenant_id: number
}

export interface UpdateTenantBillSettingsParams {
    bill_generation_day: number
    bill_send_day_offset: number
    bill_due_day_offset: number
}

interface CreateBillItemParams {
    bill_id: number
    description: string
    amount: number
}

export const useRent = () => {
    const queryClient = useQueryClient()
    const [
        stripeConnectAccount,
        setStripeConnectAccount,
    ] = useState<StripeConnectAccount | null>(null)

    const [
        stripeAccountStatus,
        setStripeAccountStatus,
    ] = useState<StripeAccountStatus | null>(null)

    const fetchTenantBills = async (params: TenantBillsParams = {}) => {
        const page = params.page || 1
        const pageSize = params.page_size || 10
        const offset = (page - 1) * pageSize

        const response = await axiosInstance.get(
            '/rent/apartment-tenant-bills/',
            {
                params: {
                    limit: pageSize,
                    offset: offset,
                    ...(params.apartment_id && {
                        apartment_id: params.apartment_id,
                    }),
                    ...(params.tenant_id && { tenant_id: params.tenant_id }),
                    ...(params.status && { status: params.status }),
                    ...(params.search && { search: params.search }),
                    ...(params.sort && { sort: params.sort }),
                    ...(params.billing_month && {
                        billing_month: params.billing_month,
                    }),
                    ...(params.payment_status && {
                        payment_status: params.payment_status,
                    }),
                    ...(params.is_overdue && { is_overdue: params.is_overdue }),
                    ...(params.due_date_start && {
                        due_date_start: params.due_date_start,
                    }),
                    ...(params.due_date_end && {
                        due_date_end: params.due_date_end,
                    }),
                    ...(params.created_date_start && {
                        created_date_start: params.created_date_start,
                    }),
                    ...(params.created_date_end && {
                        created_date_end: params.created_date_end,
                    }),
                    ...(params.min_amount && { min_amount: params.min_amount }),
                    ...(params.max_amount && { max_amount: params.max_amount }),
                    ...(params.min_amount_paid && {
                        min_amount_paid: params.min_amount_paid,
                    }),
                    ...(params.max_amount_paid && {
                        max_amount_paid: params.max_amount_paid,
                    }),
                    ...(params.min_balance_due && {
                        min_balance_due: params.min_balance_due,
                    }),
                    ...(params.max_balance_due && {
                        max_balance_due: params.max_balance_due,
                    }),
                },
            },
        )
        return response.data as TenantBillsResponse
    }

    const useTenantBills = (params: TenantBillsParams = {}) => {
        return useQuery<TenantBillsResponse, Error>({
            queryKey: ['tenant-bills', params],
            queryFn: () => fetchTenantBills(params),
            keepPreviousData: true,
        })
    }

    const CreateStripeConnectAccount = async () => {
        const response = await axiosInstance.post(
            '/rent/connect-accounts/create-account/',
        )
        window.location.href = response.data.account_link
    }

    const fetchStripeConnectAccount = useCallback(async (): Promise<StripeConnectAccount | null> => {
        const response = await axiosInstance.get(
            '/rent/connect-accounts/get-account/',
        )
        if (response.status === 204) {
            setStripeAccountStatus('restricted_soon')
            setStripeConnectAccount(null)
            return null
        }
        setStripeConnectAccount(response.data)
        setStripeAccountStatus(response.data.status as StripeAccountStatus)
        return response.data
    }, [])

    const updateStipeConnectAccount = async (params: StripeConnectAccount) => {
        const response = await axiosInstance.post(
            '/rent/connect-accounts/update-account/',
            {
                ...params,
            },
        )
        setStripeConnectAccount(response.data)
        return response.data
    }

    const fetchRentAnalytics = async () => {
        const response = await axiosInstance.get(
            '/rent/apartment-tenant-bills/get-initial-analytics/',
        )
        return response.data as RentAnalytics
    }

    const useRentAnalytics = () => {
        return useQuery<RentAnalytics, Error>({
            queryKey: ['rent-analytics'],
            queryFn: fetchRentAnalytics,
        })
    }

    const fetchDelinquentAccounts = async () => {
        const response = await axiosInstance.get(
            '/rent/apartment-tenant-bills/get-delinquent-accounts/',
        )
        return response.data as DelinquentAccount[]
    }

    const useDelinquentAccounts = () => {
        return useQuery<DelinquentAccount[], Error>({
            queryKey: ['delinquent-accounts'],
            queryFn: fetchDelinquentAccounts,
        })
    }

    const createPayment = async (params: CreatePaymentParams) => {
        const response = await axiosInstance.post(
            '/rent/apartment-tenant-bills/create-payment/',
            {
                bill_id: params.bill_id,
                amount: params.amount,
                payment_method: params.payment_method,
                notes: params.notes || '',
            },
        )
        // Invalidate the tenant bills query to trigger a refetch
        await queryClient.invalidateQueries(['tenant-bills'])
        return response.data
    }

    const voidBill = async (params: VoidBillParams) => {
        const response = await axiosInstance.post(
            `/rent/apartment-tenant-bills/${params.bill_id}/void-bill/`,
            {
                void_reason: params.void_reason || 'Voided by apartment',
            },
        )
        // Invalidate the tenant bills query to trigger a refetch
        await queryClient.invalidateQueries(['tenant-bills'])
        return response.data
    }

    const voidBillItem = async (params: VoidBillItemParams) => {
        const response = await axiosInstance.post(
            `/rent/apartment-tenant-bills/${params.bill_id}/void-bill-item/`,
            {
                bill_item_id: params.bill_item_id,
                void_reason: params.void_reason || 'Item voided by apartment',
            },
        )
        // Invalidate the tenant bills query to trigger a refetch
        await queryClient.invalidateQueries(['tenant-bills'])
        return response.data
    }

    const voidPayment = async (params: VoidPaymentParams) => {
        const response = await axiosInstance.post(
            `/rent/apartment-tenant-bills/${params.bill_id}/void-payment/`,
            {
                payment_id: params.payment_id,
                void_reason:
                    params.void_reason || 'Payment voided by apartment',
            },
        )
        // Invalidate the tenant bills query to trigger a refetch
        await queryClient.invalidateQueries(['tenant-bills'])
        return response.data
    }

    const createBillItem = async (params: CreateBillItemParams) => {
        const response = await axiosInstance.post(
            `/rent/apartment-tenant-bills/${params.bill_id}/add-bill-item/`,
            {
                description: params.description,
                amount: params.amount,
            },
        )
        // Invalidate the tenant bills query to trigger a refetch
        await queryClient.invalidateQueries(['tenant-bills'])
        return response.data as TenantBill
    }

    const sendOverdueNotice = async (params: SendOverdueNoticeParams) => {
        const response = await axiosInstance.post(
            '/rent/apartment-tenant-bills/send-overdue-notice/',
            {
                tenant_id: params.tenant_id,
            },
        )
        return response.data
    }

    const updateTenantBillSettings = async (
        params: UpdateTenantBillSettingsParams,
    ) => {
        const response = await axiosInstance.post(
            '/rent/apartment-tenant-bills/update-tenant-bill-settings/',
            {
                bill_generation_day: params.bill_generation_day,
                bill_send_day_offset: params.bill_send_day_offset,
                bill_due_day_offset: params.bill_due_day_offset,
            },
        )
        return response.data as Company
    }

    const createStripeLoginLink = async () => {
        try {
            const response = await axiosInstance.post(
                '/rent/connect-accounts/create-account-login-link/',
            )
            return response.data.url
        } catch (error: any) {
            console.error('Error creating Stripe login link:', error)
            throw error
        }
    }

    return {
        stripeConnectAccount,
        stripeAccountStatus,
        CreateStripeConnectAccount,
        fetchStripeConnectAccount,
        updateStipeConnectAccount,
        useTenantBills,
        fetchTenantBills,
        useRentAnalytics,
        fetchRentAnalytics,
        useDelinquentAccounts,
        fetchDelinquentAccounts,
        createPayment,
        voidBill,
        voidBillItem,
        voidPayment,
        createBillItem,
        sendOverdueNotice,
        updateTenantBillSettings,
        createStripeLoginLink,
    }
}
